import { concat, get, has, isArray, isEmpty, isString, uniq } from "lodash";
import { applyFunction } from "./action";
import { IAction, IExpression, IGetValue, IModel, IQuery, IResult, ISelectOption, IValue } from "./types";

const transformResponse = (data: any, action: IAction | IExpression, model: IModel, getValue?: IGetValue): any => {
  /* eslint-disable no-new-func */
  return Function.apply(null, [
    "data",
    ...(typeof action === 'string' ? [] : action.sources?.map((s) => s.split(".").pop() as string)),
    `return ${typeof action === 'string' ? action : action.expression};`,
  ])(data, ...(typeof action === 'string' ? [] : action.sources.map((s) => (getValue && getValue(s, model)) || get(model, s))));
};

/**
 * Build a URL from a query.
 * @param model
 * @param url
 * @param getValue
 * @param result
 * @returns
 */
export const buildUrl = (model: IModel, url: IAction | string, getValue?: IGetValue, result?: IResult) => {
  return isString(url) ? url : (applyFunction(model, url, getValue, result) as string);
};

export class QueryNotReadyError extends Error {
  constructor() {
    super("Query is not ready.");
    this.name = "QueryNotReadyError";
  }
}

const getQuerySources = (query: IQuery): string[] =>
  uniq(concat(get(query, ["url", "sources"], []), get(query, ["body", "sources"], [])));

const resolveQuerySourceValue = (
  source: string,
  model: IModel,
  getValue?: IGetValue,
  result?: IResult
) => {
  const provided = getValue?.(source, model, result);
  if (provided !== undefined) {
    return provided;
  }

  const fromResult = get(result, source);
  if (fromResult !== undefined) {
    return fromResult;
  }

  return get(model, source);
};

export const isQueryReady = (model: IModel, query: IQuery, getValue?: IGetValue, result?: IResult): boolean => {
  const sources = getQuerySources(query);
  if (!sources.length) {
    return true;
  }

  return sources.every((source) => {
    const value = resolveQuerySourceValue(source, model, getValue, result);
    if (value === undefined || value === null) {
      return false;
    }

    if (typeof value === "string" && value.trim() === "") {
      return false;
    }

    return true;
  });
};

const fetchFromRemoteAndModel = async (model: IModel, query: IQuery, getValue?: IGetValue, result?: IResult): Promise<any> => {
  let data = [];
  if (query.url) {
    const url = buildUrl(model, query.url, getValue, result);
    let options = undefined;
    switch (query.method) {
      case "get":
        break;
      case "post":
        options = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body:
            query.body &&
            (isString(query.body) ? query.body : (applyFunction(model, query.body, getValue, result) as string)),
        };
        break;
      default:
        throw new Error(`Unhandled query method defined in config: ${query.method}`);
    }
    const response = await fetch(url, options);
    data = await response.json();
    if (!isEmpty(data?.error || data?.fault)) {
      throw new Error(data?.error || data?.fault);
    }
  }
  return query.transform ? transformResponse(data, query.transform, model, getValue) : data;
};

/**
 * Fetch the options list for a select that is populated using an async server method.
 * @param model
 * @param query
 * @param getValue
 * @param result
 * @returns
 */
export const fetchOptions = async (
  model: IModel,
  query: IQuery,
  getValue?: IGetValue,
  result?: IResult
): Promise<Array<ISelectOption>> => {
  if (!isQueryReady(model, query, getValue, result)) {
    return [];
  }

  return fetchFromRemoteAndModel(model, query, getValue, result).then((result) => {
    if (!isArray(result) || result.find((v) => !has(v, "value") || !has(v, "label"))) {
      throw new Error("Select query must return an array of objects with value and label fields.");
    }
    return result;
  });
};

/**
 * Fetch the value for a calculate that uses an async server method.
 * @param model
 * @param query
 * @param getValue
 * @param result
 * @returns
 */
export const fetchCalculate = async (
  model: IModel,
  query: IQuery,
  getValue?: IGetValue,
  result?: IResult
): Promise<IValue> => {
  if (!isQueryReady(model, query, getValue, result)) {
    throw new QueryNotReadyError();
  }

  return fetchFromRemoteAndModel(model, query, getValue, result);
};
